"use client";

import { Loader, ArrowRight } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function InquiryFormFields() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border border-border p-8 rounded-lg bg-white shadow-md">
        <p className="font-default text-2xl text-foreground mb-4">{successMessage}</p>
        <p className="font-sans text-[11px] uppercase tracking-widest text-muted-foreground mb-6">
          A member of our team will be in touch shortly to discuss how we can support your family.
        </p>
        <Button onClick={resetForm} variant="outline" className="font-default text-[11px] uppercase tracking-widest rounded-lg border-primary bg-transparent hover:bg-accent">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-10">
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-default text-[11px] uppercase tracking-widest text-primary">
              01 / Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Your name" {...field} className="border-0 border-b border-border rounded-md bg-transparent px-0 h-10 text-base text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-primary focus-visible:shadow-md transition-colors" />
            </FormControl>
            <FormMessage className="font-sans text-[11px]" />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-default text-[11px] uppercase tracking-widest text-primary">
              02 / Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="you@example.com" {...field} className="border-0 border-b border-border rounded-md bg-transparent px-0 h-10 text-base text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-primary focus-visible:shadow-md transition-colors" />
            </FormControl>
            <FormMessage className="font-sans text-[11px]" />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-default text-[11px] uppercase tracking-widest text-primary">
              03 / Phone Number
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 555-123-4567" {...field} className="border-0 border-b border-border rounded-md bg-transparent px-0 h-10 text-base text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-primary focus-visible:shadow-md transition-colors" />
            </FormControl>
            <p className="font-sans text-[10px] uppercase tracking-widest text-muted-foreground">
              Optional, but helps us get back to you faster
            </p>
            <FormMessage className="font-sans text-[11px]" />
          </FormItem>} />

      <FormField control={control} name="inquiry_type" rules={{
      required: "Please select an inquiry type"
    }} render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-default text-[11px] uppercase tracking-widest text-primary">
              04 / How can we help?
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="w-full border-0 border-b border-border rounded-md bg-transparent px-0 h-10 text-base text-foreground focus-visible:ring-0 focus-visible:border-primary data-[placeholder]:text-muted-foreground/60">
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-white border border-border rounded-lg">
                <SelectItem value="Respite Care">Respite Care</SelectItem>
                <SelectItem value="Advocacy Support">Advocacy Support</SelectItem>
                <SelectItem value="Inclusive Programs">Inclusive Programs</SelectItem>
                <SelectItem value="Volunteer Inquiry">Volunteer Inquiry</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage className="font-sans text-[11px]" />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Please provide at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-default text-[11px] uppercase tracking-widest text-primary">
              05 / Tell us about your family's needs
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Share a bit about your situation so we can best support you..." {...field} rows={5} className="border-0 border-b border-border rounded-md bg-transparent px-0 py-2 text-base text-foreground placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:border-primary focus-visible:shadow-md transition-colors resize-none min-h-[120px]" />
            </FormControl>
            <FormMessage className="font-sans text-[11px]" />
          </FormItem>} />

      {submitError && <div className="border border-destructive/40 p-4 rounded-lg">
          <p className="font-sans text-[11px] uppercase tracking-widest text-destructive">{submitError}</p>
        </div>}

      <div className="pt-4 space-y-6">
        <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto rounded-lg bg-secondary text-secondary-foreground hover:bg-primary px-8 h-12 font-default text-[11px] uppercase tracking-widest shadow-lg transition-all">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Submitting
            </> : <>
              Send Message
              <ArrowRight className="ml-2 size-4" />
            </>}
        </Button>
        <p className="font-sans text-[10px] uppercase tracking-widest text-muted-foreground">
          We'll respond within 48 hours · Your inquiry is confidential
        </p>
      </div>
    </Form>;
}
export default function ReachOutToOurTeam() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const contactInfo = [{
    label: "Office",
    value: "123 Community Way, Hope City, USA"
  }, {
    label: "Phone",
    value: "+1 555-012-3456"
  }, {
    label: "Email",
    value: "hello@hopetorchlighters.org"
  }, {
    label: "Hours",
    value: "Mon-Fri: 9am - 5pm"
  }];
  return <section data-section-id="3673" id="reach-out-to-our-team" className="bg-background text-foreground pt-32 md:pt-[200px] pb-24 md:pb-32 px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={leftRef} className={`lg:col-span-5 lg:sticky lg:top-32 lg:self-start transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="font-default text-[11px] uppercase tracking-widest text-secondary mb-8">
              — Connect / 01
            </p>
            <h2 className="font-default font-semibold text-4xl md:text-5xl lg:text-[72px] leading-[1.05] text-foreground tracking-tight">
              Let's light the way <span className="italic text-secondary">together</span>.
            </h2>
            <p className="mt-8 text-foreground/80 text-[17px] leading-relaxed max-w-[420px]">
              Every family deserves a community that understands and supports them. Reach out to share your story, ask about our programs, or learn how you can partner with us in serving neurodiverse families.
            </p>

            <div className="mt-16 space-y-0">
              {contactInfo.map((info, i) => <div key={i} data-index={i} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6 py-5 border-t border-border last:border-b">
                  <span className="font-default text-[11px] uppercase tracking-widest text-primary shrink-0 sm:w-24">
                    {info.label}
                  </span>
                  <span className="font-sans text-[11px] uppercase tracking-widest text-foreground/80">
                    {info.value}
                  </span>
                </div>)}
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 lg:col-start-7 transition-all duration-700 ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <FormProvider formKey="reach_out_form_connect" formId="reach_out_form_connect" sectionName="reach-out-to-our-team" defaultValues={{
            name: "",
            email: "",
            phone: "",
            inquiry_type: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {},
            inquiry_type: {
              required: true
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Send Message" successMessage="Thank you for reaching out. We look forward to connecting with you." errorMessage="We were unable to submit your inquiry. Please try again.">
              <InquiryFormFields />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}